import React from 'react';
import { Flex, Text, Heading, Box, Stack, Icon, Button, useColorModeValue } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaDatabase, FaCarCrash, FaRuler } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { Trans } from '@lingui/macro';

interface Query {
  type: string;
  count: number;
  amount: string;
}

interface QueriesCardProps {
  title: string;
  damageQueries: Query;
  kmQueries: Query;
  totalQueries: Query;
  onQuery: () => void;
}

interface QueryItemProps {
  icon: IconType | React.ComponentType;
  type: string;
  count: number;
  amount: string;
  labelColor: string;
  valueColor: string;
  accentColor?: string;
  fontWeight?: string | number;
}

// Helper component for query items
const QueryItem: React.FC<QueryItemProps> = ({
  icon,
  type,
  count,
  amount,
  labelColor,
  valueColor,
  accentColor = 'blue.500',
  fontWeight = 'medium',
}) => {
  return (
    <Flex justify="space-between" align="center">
      <Flex align="center">
        <Icon as={icon} color={accentColor} mr={2} />
        <Text color={labelColor} fontWeight={fontWeight}>
          {type}
        </Text>
      </Flex>
      <Flex align="center" fontWeight={fontWeight} color={valueColor}>
        <Text mr={2}>
          {count} <Trans>Sorgu</Trans>
        </Text>
        <Text>{amount}</Text>
      </Flex>
    </Flex>
  );
};

export const QueriesCard: React.FC<QueriesCardProps> = ({ title, damageQueries, kmQueries, totalQueries, onQuery }) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const buttonBg = useColorModeValue('teal.500', 'teal.600');
  const buttonHoverBg = useColorModeValue('teal.600', 'teal.700');

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column" minHeight="300px">
      <Flex mb={4} align="center">
        <Icon as={SearchIcon} color={accentColor} mr={2} boxSize={5} />
        <Heading size="md" color={headingColor}>
          {title}
        </Heading>
      </Flex>

      <Box
        flex="1"
        bg={bgColor}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        boxShadow="sm"
        p={5}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          bg: accentColor,
        }}
      >
        <Stack spacing={5} mb={6}>
          <QueryItem
            icon={FaCarCrash}
            type={damageQueries.type}
            count={damageQueries.count}
            amount={damageQueries.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="red.500"
          />

          <QueryItem
            icon={FaRuler}
            type={kmQueries.type}
            count={kmQueries.count}
            amount={kmQueries.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="orange.500"
          />

          <QueryItem
            icon={FaDatabase}
            type={totalQueries.type}
            count={totalQueries.count}
            amount={totalQueries.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="purple.500"
            fontWeight="bold"
          />
        </Stack>

        <Button
          leftIcon={<SearchIcon />}
          w="100%"
          size="md"
          color="white"
          bg={buttonBg}
          _hover={{ bg: buttonHoverBg }}
          borderRadius="md"
          fontWeight="semibold"
          boxShadow="sm"
          onClick={onQuery}
        >
          <Trans>Sorgulama Yap</Trans>
        </Button>
      </Box>
    </Box>
  );
};
